(() => {
  const bbNormalize = (value) => String(value ?? "")
    .trim()
    .toLocaleLowerCase("es")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");

  class BBSizeAssistant extends HTMLElement {
    connectedCallback() {
      if (this.abortController && !this.abortController.signal.aborted) return;

      this.abortController = new AbortController();
      this.profile = this.dataset.profile || "general";
      this.profileKey = this.dataset.profileKey || this.profile;
      this.profileType = this.dataset.profileType || "footwear";
      this.formId = this.dataset.formId;
      this.optionPosition = String(this.dataset.optionPosition || "");
      this.rememberMeasurement = this.dataset.rememberMeasurement === "true";
      this.modifierNote = this.dataset.modifierNote || "Para darte un ajuste más cómodo, recomendamos la talla [talla].";
      this.modifierMaximumNote = this.dataset.modifierMaximumNote || "Estás en la talla más alta de esta guía. Confirma el ajuste con una asesora antes de comprar.";

      const modalId = this.querySelector("[data-bb-size-open]")?.getAttribute("aria-controls");
      this.modal = this.querySelector("[data-bb-size-modal]") || this.modal || (modalId ? document.getElementById(modalId) : null);
      this.scope = this.modal || this;
      this.maps = this.parseMaps();
      this.activeMethod = this.maps[this.activeMethod]?.length ? this.activeMethod : Object.keys(this.maps).find((method) => this.maps[method].length);
      this.drafts = this.drafts || new Map();
      this.recommendedSize = null;
      this.selecting = false;

      this.entryElement = this.scope.querySelector("[data-bb-entry]");
      this.guideElement = this.scope.querySelector("[data-bb-guide]");
      this.form = this.scope.querySelector("[data-bb-size-form]");
      this.measurementInput = this.scope.querySelector("[data-bb-measurement]");
      this.measurementLabel = this.scope.querySelector("[data-bb-measurement-label]");
      this.helpElement = this.scope.querySelector("[data-bb-measurement-help]");
      this.modifierInput = this.scope.querySelector("[data-bb-modifier]");
      this.errorElement = this.scope.querySelector("[data-bb-error]");
      this.resultElement = this.scope.querySelector("[data-bb-result]");
      this.resultSizeElement = this.scope.querySelector("[data-bb-result-size]");
      this.selectSizeLabel = this.scope.querySelector("[data-bb-select-size-label]");
      this.selectButton = this.scope.querySelector("[data-bb-select-size]");
      this.availabilityElement = this.scope.querySelector("[data-bb-availability]");
      this.modifierNoteElement = this.scope.querySelector("[data-bb-modifier-note]");
      this.resultHeading = this.scope.querySelector("[data-bb-result-heading]");
      this.measurementSummary = this.scope.querySelector("[data-bb-measurement-summary]");
      this.editButton = this.scope.querySelector("[data-bb-edit]");
      this.selectionErrorElement = this.scope.querySelector("[data-bb-selection-error]");
      this.selectCopy = this.scope.querySelector("[data-bb-select-copy]");
      this.selectPending = this.scope.querySelector("[data-bb-select-pending]");
      this.methodButtons = Array.from(this.scope.querySelectorAll("[data-bb-method]"));
      this.methodPanels = Array.from(this.scope.querySelectorAll("[data-bb-method-panel]"));

      if (!this.form || !this.measurementInput || !this.modal || !this.entryElement || !this.maps[this.activeMethod]?.length || !this.profileMatchesProduct()) {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      this.methodButtons.forEach((button) => {
        button.addEventListener("click", () => this.setMethod(button.dataset.bbMethod), { signal: this.abortController.signal });
      });

      this.form.addEventListener("submit", (event) => this.handleSubmit(event), { signal: this.abortController.signal });
      this.measurementInput.addEventListener("input", () => {
        this.clearError();
        this.resetResult();
      }, { signal: this.abortController.signal });
      this.modifierInput?.addEventListener("change", () => this.resetResult(), { signal: this.abortController.signal });
      this.selectButton?.addEventListener("click", () => this.selectRecommendedSize(), { signal: this.abortController.signal });
      this.editButton?.addEventListener("click", () => {
        if (this.selecting) return;
        this.resetResult();
        this.resetScroll();
        this.measurementInput.focus({ preventScroll: true });
      }, { signal: this.abortController.signal });
      this.querySelector("[data-bb-size-open]")?.addEventListener("click", () => this.emitEvent("bb:size-assistant-open"), { signal: this.abortController.signal });
      this.modal.addEventListener("dialog:before-show", () => {
        if (this.recommendedSize) this.updateAvailability(this.recommendedSize);
        this.resetScroll();
      }, { signal: this.abortController.signal });
      document.addEventListener("variant:change", (event) => {
        if (event.target === document.getElementById(this.formId) && this.recommendedSize) {
          this.updateAvailability(this.recommendedSize);
        }
      }, { signal: this.abortController.signal });

      this.restoreMeasurement();
      this.updateMethodUi();
      this.resetResult();
    }

    disconnectedCallback() {
      if (this.measurementInput && this.activeMethod) {
        this.drafts.set(this.activeMethod, {
          measurement: this.measurementInput.value,
          modifier: Boolean(this.modifierInput?.checked)
        });
      }
      this.abortController?.abort();
      queueMicrotask(() => {
        if (!this.isConnected && this.modal?.parentElement === document.body) {
          if (this.modal.open && typeof this.modal.hide === "function") this.modal.hide();
          this.appendChild(this.modal);
        }
      });
    }

    parseMaps() {
      const maps = {};
      const scripts = this.scope?.querySelectorAll("script[data-bb-size-map]") || this.querySelectorAll("script[data-bb-size-map]");

      scripts.forEach((element) => {
        let source = "";

        try {
          source = JSON.parse(element.textContent || "\"\"");
        } catch (_error) {
          source = "";
        }

        maps[element.dataset.bbSizeMap] = String(source)
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter((line) => line && !line.startsWith("#"))
          .map((line) => {
            const parts = line.split("|").map((part) => part?.trim());
            if (parts.length !== 3 && parts.length !== 4) return null;
            const legacy = parts.length === 3;
            const [size, adjustedSize, minimum, maximum] = legacy
              ? [parts[0], "", parts[1], parts[2]]
              : [parts[0], parts[1], parts[2], parts[3]];

            return {
              size,
              adjustedSize,
              minimum: minimum ? Number(minimum.replace(",", ".")) : NaN,
              maximum: maximum ? Number(maximum.replace(",", ".")) : NaN
            };
          })
          .filter((row) => row?.size && Number.isFinite(row.minimum) && Number.isFinite(row.maximum) && row.minimum <= row.maximum)
          .sort((a, b) => a.minimum - b.minimum);
      });

      return maps;
    }

    profileMatchesProduct() {
      const configuredSizes = new Set(Object.values(this.maps).flat().flatMap((row) => [row.size, row.adjustedSize]).filter(Boolean).map(bbNormalize));
      const productSizes = this.getSizeInputs().map((input) => bbNormalize(input.dataset.optionLabel || input.value));
      return productSizes.length === 0 || productSizes.some((size) => configuredSizes.has(size));
    }

    setMethod(method) {
      if (this.selecting || !this.maps[method]?.length || method === this.activeMethod) return;

      this.drafts.set(this.activeMethod, {
        measurement: this.measurementInput.value,
        modifier: Boolean(this.modifierInput?.checked)
      });
      this.activeMethod = method;
      this.measurementInput.value = "";
      if (this.modifierInput) this.modifierInput.checked = false;
      this.resetResult();
      this.clearError();
      this.restoreMeasurement();
      this.updateMethodUi();
    }

    updateMethodUi() {
      const activeMap = this.maps[this.activeMethod] || [];
      if (!activeMap.length) return;

      this.methodButtons.forEach((button) => {
        button.disabled = !this.maps[button.dataset.bbMethod]?.length;
        button.setAttribute("aria-pressed", String(button.dataset.bbMethod === this.activeMethod));
      });

      this.methodPanels.forEach((panel) => {
        const isActive = panel.dataset.bbMethodPanel === this.activeMethod;
        panel.hidden = !isActive;
        panel.setAttribute("aria-hidden", String(!isActive));
      });

      const activeButton = this.methodButtons.find((button) => button.dataset.bbMethod === this.activeMethod);
      const label = activeButton?.dataset.bbInputLabel || this.measurementLabel.textContent;
      const help = activeButton?.dataset.bbInputHelp || this.helpElement.textContent;
      const minimum = Math.min(...activeMap.map((row) => row.minimum));
      const maximum = Math.max(...activeMap.map((row) => row.maximum));

      this.measurementLabel.textContent = label;
      this.helpElement.textContent = help;
      this.measurementInput.placeholder = `Entre ${this.formatMeasurement(minimum)} y ${this.formatMeasurement(maximum)}`;
    }

    handleSubmit(event) {
      event.preventDefault();
      if (this.selecting) return;
      this.clearError();
      this.resetResult();

      const value = this.measurementInput.value.trim();
      const measurement = Number(value.replace(",", "."));
      const activeMap = this.maps[this.activeMethod] || [];

      if (!/^(?:\d+(?:[.,]\d+)?|[.,]\d+)$/.test(value) || !Number.isFinite(measurement) || measurement <= 0) {
        this.showError("Ingresa la medida en centímetros. Por ejemplo: 27,3.");
        this.measurementInput.focus({ preventScroll: true });
        return;
      }

      const baseIndex = activeMap.findIndex((row) => measurement >= row.minimum && measurement <= row.maximum);

      if (baseIndex === -1) {
        this.showError("La medida no coincide con este perfil. Revisa la medición o solicita asesoría antes de comprar.");
        this.measurementInput.focus({ preventScroll: true });
        return;
      }

      const baseRow = activeMap[baseIndex];
      const modifierApplied = Boolean(this.modifierInput?.checked);
      let recommendedSize = baseRow.size;
      let atMaximum = false;

      if (modifierApplied) {
        if (baseRow.adjustedSize) {
          recommendedSize = baseRow.adjustedSize;
        } else if (activeMap[baseIndex + 1]?.size) {
          recommendedSize = activeMap[baseIndex + 1].size;
        } else {
          atMaximum = true;
        }
      }

      this.recommendedSize = recommendedSize;
      this.resultSizeElement.textContent = recommendedSize;
      this.selectSizeLabel.textContent = recommendedSize;

      if (this.modifierNoteElement) {
        this.modifierNoteElement.hidden = !modifierApplied;
        this.modifierNoteElement.textContent = modifierApplied
          ? this.interpolate(atMaximum ? this.modifierMaximumNote : this.modifierNote, { talla: recommendedSize, talla_base: baseRow.size })
          : "";
      }

      this.updateAvailability(recommendedSize);
      this.measurementInput.blur();
      this.guideElement.open = false;
      this.entryElement.hidden = true;
      this.resultElement.hidden = false;
      const methodLabel = this.methodButtons.find((button) => button.dataset.bbMethod === this.activeMethod)?.querySelector("strong")?.textContent || this.measurementLabel.textContent;
      this.measurementSummary.textContent = `${methodLabel} · ${this.formatMeasurement(measurement)} cm`;
      this.resetScroll();
      this.resultHeading.focus({ preventScroll: true });
      this.saveMeasurement(measurement);
      this.emitEvent("bb:size-recommendation", {
        method: this.activeMethod,
        measurement,
        baseSize: baseRow.size,
        size: recommendedSize,
        modifierApplied
      });
    }

    updateAvailability(size) {
      const input = this.findSizeInput(size);
      this.selectButton.disabled = this.selecting;
      this.availabilityElement.dataset.state = "available";

      if (!input) {
        this.availabilityElement.textContent = `La talla ${size} no está configurada en este producto.`;
        this.availabilityElement.dataset.state = "unavailable";
        this.selectButton.disabled = true;
        return;
      }

      if (input.dataset.optionAvailable === "false" || input.disabled) {
        this.availabilityElement.textContent = `La talla ${size} no está disponible en la combinación elegida.`;
        this.availabilityElement.dataset.state = "unavailable";
        this.selectButton.disabled = true;
        return;
      }

      this.availabilityElement.textContent = `La talla ${size} está disponible.`;
    }

    async selectRecommendedSize() {
      if (!this.recommendedSize || this.selecting) return;

      this.updateAvailability(this.recommendedSize);
      if (this.selectButton.disabled) return;
      const input = this.findSizeInput(this.recommendedSize);
      const picker = this.getPicker();
      const productForm = document.getElementById(this.formId);
      const size = this.recommendedSize;
      if (!input || !picker || !productForm) return;

      this.selectionErrorElement.hidden = true;
      this.setSelecting(true);
      try {
        if (typeof picker.selectCombination !== "function") throw new Error("Variant picker unavailable");
        if (!input.checked) {
          const options = Array.from(productForm.elements)
            .filter((option) => option.matches("input[data-option-position]:checked") && option.dataset.optionPosition !== this.optionPosition);
          options.push(input);
          const optionValues = options.sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map((option) => option.value);
          await picker.selectCombination({ optionValues, productChange: input.hasAttribute("data-product-url") });
        }

        const selectedInput = this.findSizeInput(size);
        if (!selectedInput?.checked || !picker.selectedVariant?.id || picker.selectedVariant.available === false) {
          throw new Error("Variant selection not confirmed");
        }
        if (this.modal.open) await this.modal.hide();
        this.emitEvent("bb:size-selected", { size });
      } catch (_error) {
        if (this.isConnected) {
          this.selectionErrorElement.textContent = "No pudimos confirmar la selección. Revisa tu conexión e inténtalo de nuevo, o elige la talla en el producto.";
          this.selectionErrorElement.hidden = false;
        }
      } finally {
        this.setSelecting(false);
        if (this.recommendedSize) this.updateAvailability(this.recommendedSize);
      }
    }

    setSelecting(pending) {
      this.selecting = pending;
      this.selectButton.disabled = pending;
      this.selectButton.setAttribute("aria-busy", String(pending));
      this.selectCopy.hidden = pending;
      this.selectPending.hidden = !pending;
      this.editButton.disabled = pending;
    }

    getPicker() {
      return document.querySelector(`variant-picker[form-id="${CSS.escape(this.formId)}"]`);
    }

    getSizeInputs() {
      const picker = this.getPicker();
      if (!picker) return [];

      return Array.from(picker.querySelectorAll("input[data-option-position]"))
        .filter((input) => String(input.dataset.optionPosition) === this.optionPosition);
    }

    findSizeInput(size) {
      return this.getSizeInputs().find((input) => bbNormalize(input.dataset.optionLabel || input.value) === bbNormalize(size)) || null;
    }

    resetResult() {
      this.recommendedSize = null;
      if (this.resultElement) this.resultElement.hidden = true;
      if (this.entryElement) this.entryElement.hidden = false;
      if (this.selectionErrorElement) this.selectionErrorElement.hidden = true;
    }

    resetScroll() {
      const body = this.modal?.getShadowPartByName?.("body");
      if (body) body.scrollTop = 0;
    }

    showError(message) {
      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
      this.measurementInput.setAttribute("aria-invalid", "true");
    }

    clearError() {
      this.errorElement.textContent = "";
      this.errorElement.hidden = true;
      this.measurementInput.removeAttribute("aria-invalid");
    }

    storageKey() {
      return `bb-size-assistant:v2:${this.profileKey}:${this.activeMethod}`;
    }

    restoreMeasurement() {
      const draft = this.drafts.get(this.activeMethod);
      if (draft) {
        this.measurementInput.value = draft.measurement;
        if (this.modifierInput) this.modifierInput.checked = draft.modifier;
        return;
      }
      if (!this.rememberMeasurement) return;

      try {
        const saved = JSON.parse(localStorage.getItem(this.storageKey()) || "null");
        if (Number.isFinite(saved?.measurement) && saved.measurement > 0) this.measurementInput.value = String(saved.measurement).replace(".", ",");
        if (this.modifierInput) this.modifierInput.checked = Boolean(saved?.modifier);
      } catch (_error) {
        // El asistente continúa aunque el navegador bloquee localStorage.
      }
    }

    saveMeasurement(measurement) {
      if (!this.rememberMeasurement) return;

      try {
        localStorage.setItem(this.storageKey(), JSON.stringify({
          measurement,
          modifier: Boolean(this.modifierInput?.checked)
        }));
      } catch (_error) {
        // El asistente continúa aunque el navegador bloquee localStorage.
      }
    }

    interpolate(template, values) {
      return Object.entries(values).reduce((output, [key, value]) => output.replaceAll(`[${key}]`, String(value)), template);
    }

    emitEvent(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(name, {
        detail: {
          productId: this.dataset.productId,
          productHandle: this.dataset.productHandle,
          profile: this.profile,
          profileKey: this.profileKey,
          ...detail
        }
      }));
    }

    formatMeasurement(value) {
      return Number(value).toLocaleString("es-CO", { minimumFractionDigits: 1, maximumFractionDigits: 3 });
    }
  }

  if (!window.customElements.get("bb-size-assistant")) {
    window.customElements.define("bb-size-assistant", BBSizeAssistant);
  }

  class BBSizeAvailability extends HTMLElement {
    connectedCallback() {
      if (this.abortController && !this.abortController.signal.aborted) return;

      this.abortController = new AbortController();
      this.form = document.forms[this.dataset.formId];
      this.message = this.querySelector("[data-bb-stock-message]");
      this.threshold = Number(this.dataset.threshold || 3);
      this.showExact = this.dataset.showExact === "true";

      try {
        const variants = JSON.parse(this.querySelector("[data-bb-variant-inventory]")?.textContent || "[]");
        this.variants = new Map(variants.map((variant) => [String(variant.id), variant]));
      } catch (_error) {
        this.variants = new Map();
      }

      if (!this.form || !this.message || this.variants.size === 0) {
        this.hidden = true;
        return;
      }

      this.form.addEventListener("variant:change", (event) => this.update(event.detail?.variant?.id), { signal: this.abortController.signal });
      this.form.addEventListener("change", () => window.requestAnimationFrame(() => this.updateFromForm()), { signal: this.abortController.signal });
      document.addEventListener("bb:size-selected", () => window.requestAnimationFrame(() => this.updateFromForm()), { signal: this.abortController.signal });
      this.updateFromForm();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    updateFromForm() {
      this.update(this.form?.elements?.id?.value);
    }

    update(variantId) {
      const variant = this.variants.get(String(variantId || ""));
      const quantity = Number(variant?.inventoryQuantity);
      const isTracked = variant?.inventoryManagement === "shopify";
      const stopsAtZero = variant?.inventoryPolicy === "deny";
      const isLowStock = variant?.available && quantity > 0 && quantity <= this.threshold;

      if (!variant || !isTracked || !stopsAtZero || !isLowStock) {
        this.hidden = true;
        this.message.textContent = "";
        return;
      }

      let template = this.dataset.genericText || "Pocas unidades disponibles en talla [talla]";

      if (this.showExact) {
        template = quantity === 1
          ? (this.dataset.singularText || "Último par disponible en talla [talla]")
          : (this.dataset.pluralText || "Solo quedan [cantidad] pares en talla [talla]");
      }

      this.message.textContent = template
        .replaceAll("[cantidad]", String(quantity))
        .replaceAll("[talla]", String(variant.size || ""));
      this.hidden = false;
    }
  }

  if (!window.customElements.get("bb-size-availability")) {
    window.customElements.define("bb-size-availability", BBSizeAvailability);
  }
})();